import clsx from 'clsx'
import {useFormik} from 'formik'
import {isUndefined} from 'lodash'
import {useState} from 'react'
import {useIntl} from 'react-intl'
import {Link} from 'react-router-dom'
import {CSSTransition} from 'react-transition-group'
import * as Yup from 'yup'
import {useCancelToken, UseYupValidate} from '../../../../gori/hooks'
import {Button, InputTextFormik} from '../../../../gori/widgets'
import {AuthService} from '../../auth'
import {formatApiFieldError} from '../core/helpers'

export function ForgotPassword() {
  const intl = useIntl()
  const {newCancelToken, isCancel} = useCancelToken()
  const {infoYup} = UseYupValidate()

  const [loading, setLoading] = useState(false)
  const [statusForm, setStatusForm] = useState<
    {status: 'error' | 'success'; message: string} | undefined
  >(undefined)

  const forgotPasswordSchema = Yup.object().shape({
    email: infoYup.email(),
  })

  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema: forgotPasswordSchema,
    onSubmit: async (values) => {
      setStatusForm(undefined)
      try {
        setLoading(true)
        const config = {cancelToken: newCancelToken()}
        await AuthService.resetPassword(values, config)
        setStatusForm({
          status: 'success',
          message: intl.formatMessage({id: 'PASSWORD_RESET_INSTRUCTION_HAS_BEEN_EMAILED'}),
        })
      } catch (error: any) {
        if (isCancel(error)) return
        const responseData = error?.response?.data
        // 422 field validation errors come back as {message, errors: {email: [...]}}.
        // The `email` field error may use the "<field>|<CODE>" convention shared with
        // other import/validate APIs (e.g. "email|INPUT_IS_NOT_A_VALID_EMAIL_ADDRESS"),
        // so it needs to be translated via the matching i18n key.
        const emailError = responseData?.errors?.email?.[0]
        setStatusForm({
          status: 'error',
          message: emailError
            ? formatApiFieldError(intl, emailError)
            : responseData?.message || intl.formatMessage({id: 'SOMETHING_WENT_WRONG'}),
        })
      } finally {
        setLoading(false)
      }
    },
  })

  return (
    <CSSTransition appear in timeout={300} classNames='fade' unmountOnExit>
      <div className='w-lg-500px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto'>
        <div className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'>
          <div className='text-center mb-10'>
            <h1 className='text-dark mb-3'>{intl.formatMessage({id: 'FORGOT_PASSWORD'})} ?</h1>
            <div className='text-gray-500 fw-semibold fs-6'>
              {intl.formatMessage({id: 'ENTER_YOUR_EMAIL_TO_RESET_YOUR_PASSWORD'})}
            </div>
          </div>
          {!isUndefined(statusForm) && (
            <div
              className={clsx('mb-lg-10 alert', {
                'alert-danger': statusForm.status === 'error',
                'alert-success': statusForm.status === 'success',
              })}
            >
              <div className='alert-text font-weight-bold'>{statusForm.message}</div>
            </div>
          )}
          <InputTextFormik
            className='mb-10'
            labelClassName='fw-bolder text-dark fs-6'
            label={intl.formatMessage({id: 'EMAIL'})}
            required
            formik={formik}
            name='email'
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                formik.handleSubmit()
              }
            }}
          />
          <div className='d-flex flex-wrap justify-content-center'>
            <Button
              className='btn btn-lg btn-primary fw-bolder me-4'
              label={intl.formatMessage({id: 'SUBMIT'})}
              loading={loading}
              disabled={loading}
              event={formik.submitForm}
            />
            <Link to='/auth/login'>
              <Button
                className='btn btn-lg btn-light-primary fw-bolder'
                label={intl.formatMessage({id: 'CANCEL'})}
                disabled={loading}
              />
            </Link>
          </div>
        </div>
      </div>
    </CSSTransition>
  )
}
